import { useEffect } from 'react'
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { CalendarDays, Contact, Home, ReceiptEuro, Settings, Users, X } from 'lucide-react'
import { useI18n, type TKey } from './i18n'
import { useAuth } from './auth'
import { useStore } from './data/store'
import { cx } from './ui/kit'
import { Logo } from './ui/Logo'
import TodayView from './views/TodayView'
import CalendarView from './views/CalendarView'
import BookingsView from './views/BookingsView'
import BillingView from './views/BillingView'
import BillingSetupView, { useBillingState } from './views/BillingSetupView'
import { billingStage, mandatFehlerSatz, trialDaysLeft } from './data/stripe'
import { preisJeMonat, usePaketStand } from './data/pakete'
import { kaufwegSichtbar } from './lib/kaufweg'
import SettingsView from './views/SettingsView'
import DocumentView from './views/DocumentView'
import InvoiceBundle from './views/InvoiceBundle'
import LoginView from './views/LoginView'
import OnboardingView from './views/OnboardingView'
import AvvGate from './views/AvvGate'
import AvvBanner from './views/AvvBanner'
import { KontoLoeschungGate } from './views/KontoLoeschung'
import EnquiryView from './views/EnquiryView'
import PublicDocumentView from './views/PublicDocumentView'
import LegalView from './views/LegalView'
import CheckinView from './views/CheckinView'
import GuestsView from './views/GuestsView'
import LadenAboView from './views/LadenAboView'
import { ladenAenderungenBeobachten, ladenAnspruchMelden, ladenKaufwegSichtbar } from './data/ladenAbo'
import { track } from './lib/track'

/*
 * `short` ist die Beschriftung für die Leiste am unteren Rand des Telefons.
 *
 * Nachgemessen: bei 375 Pixern bleiben je Eintrag 63 Pixel, abzüglich Abstand rund
 * 59 zum Schreiben. „Abrechnung" braucht bei lesbaren 11 Pixern 64, „Einstellungen"
 * 70. Sechs ausgeschriebene deutsche Wörter passen dort schlicht nicht — und die
 * Antwort darauf ist ein kürzeres Wort, nicht eine kleinere Schrift. Winzige Schrift
 * ist keine Beschriftung, sondern ein Muster.
 *
 * „Kasse" und „Konto" sind im Gastgewerbe die geläufigen Kurzformen. In der
 * Seitenleiste am Rechner, wo Platz ist, stehen weiterhin die vollen Wörter.
 */
const NAV: { to: string; key: TKey; short?: TKey; icon: typeof Home }[] = [
  { to: '/today', key: 'nav.today', icon: Home },
  { to: '/calendar', key: 'nav.calendar', icon: CalendarDays },
  { to: '/bookings', key: 'nav.bookings', icon: Users },
  { to: '/guests', key: 'nav.guests', icon: Contact },
  { to: '/billing', key: 'nav.billing', short: 'nav.billingShort', icon: ReceiptEuro },
  { to: '/settings', key: 'nav.settings', short: 'nav.settingsShort', icon: Settings },
]

export default function App() {
  const { t, lang, setLang } = useI18n()
  const { mode, tenantId } = useAuth()
  const store = useStore()
  // Guest-facing routes: the enquiry form and the documents linked from guest mail.
  const path = useLocation().pathname

  /*
   * „Hat sich jemand tatsächlich durchgeklickt?"
   *
   * `demo_open` zählt jeden, der die Demo betritt — auch den, der sofort wieder
   * geht. Erst der WECHSEL auf eine zweite Ansicht sagt, dass die Demo benutzt
   * und nicht nur geöffnet wurde. Der Abstand zwischen beiden Zahlen ist die
   * eigentliche Auskunft.
   *
   * BEIDE HÄNGEN AM ZUSTAND, NICHT AM KLICK. `demo_open` stand bis zum
   * 30.07.2026 in `enterDemo`, also in dem Klick, mit dem die Demo betreten
   * wird. Der Demo-Zustand wird beim Start aber aus `localStorage`
   * wiederhergestellt: wer die Demo schon einmal geöffnet hatte, landete beim
   * nächsten Aufruf direkt darin, ohne diesen Klick — und wurde nie wieder
   * gezählt. Gemessen wurde damit „dieses Gerät hat sich irgendwann einmal
   * entschieden" statt „schaut heute hin", und eine Prüfung von einem anderen
   * Netz aus blieb unsichtbar, weil `localStorage` an der Domain hängt und
   * nicht am Netz.
   *
   * Doppelt zählt es deshalb trotzdem nicht: `track` meldet je Reiter einmal,
   * und der Tages-Hash im Server fasst alle Aufrufe desselben Menschen an
   * demselben Tag zu einem Besucher zusammen.
   */
  useEffect(() => {
    if (mode === 'demo') track('demo_open')
  }, [mode])

  useEffect(() => {
    if (mode === 'demo' && path !== '/' && path !== '/today') track('demo_use')
  }, [mode, path])

  /*
   * Der zweite, kostenlose Abgleichskanal des Laden-Abos (Vorgang 009, AP10/
   * AK13): Beim App-Start meldet die Hülle den Storefront-Preis und reicht
   * ihre StoreKit-Ansprüche bei `apple-purchase` ein — idempotent, derselbe
   * Aufruf wie „Käufe wiederherstellen". Fehler verschluckt das Modul selbst:
   * ein App-Start darf an einer Meldung nie scheitern.
   */
  useEffect(() => {
    if (mode === 'live' && tenantId && ladenKaufwegSichtbar()) {
      void ladenAnspruchMelden(tenantId).then((ergebnis) => {
        if (ergebnis.zustand === 'active') store.reload()
      })
    }
  }, [mode, tenantId])

  useEffect(() => {
    if (mode !== 'live' || !tenantId || !ladenKaufwegSichtbar()) return
    let beendet = false
    let abmelden: (() => Promise<void>) | undefined
    void ladenAenderungenBeobachten(tenantId, (ergebnis) => {
      if (!beendet && ergebnis.zustand === 'active') store.reload()
    }).then((listener) => {
      if (beendet) void listener.remove()
      else abmelden = listener.remove
    }).catch(() => {})
    return () => { beendet = true; if (abmelden) void abmelden() }
  }, [mode, tenantId])

  const publicPath = ['/e/', '/q/', '/i/', '/checkin/'].some((p) => path.startsWith(p))
    // Impressum und Datenschutzerklärung müssen „ständig zugänglich" sein (§ 5 ECG,
    // Art. 13 DSGVO) — auch für jemanden ohne Konto und ohne Anmeldung.
    || ['/impressum', '/datenschutz', '/legal', '/privacy', '/agb', '/terms',
        '/rechtliches.html', '/en/legal.html',
        // Google Play verlangt seit 2023 eine Löschseite, die OHNE App und OHNE
        // Anmeldung erreichbar ist — sonst wird die App abgelehnt. Sie gehört
        // aus demselben Grund hierher wie Impressum und Datenschutz: wer sein
        // Konto loswerden will, kommt womöglich gerade nicht mehr hinein.
        '/konto-loeschen', '/delete-account'].includes(path)

  // The guest-facing enquiry form is public and must render before the auth gate —
  // a guest has no account and must never see a login screen.
  if (publicPath) {
    return (
      <Routes>
        <Route path="/e/:slug" element={<EnquiryView />} />
        <Route path="/q/:token" element={<PublicDocumentView />} />
        <Route path="/i/:token" element={<PublicDocumentView />} />
        <Route path="/checkin/:token" element={<CheckinView />} />
        <Route path="/rechtliches.html" element={<Navigate to="/impressum" replace />} />
        <Route path="/en/legal.html" element={<LegalView />} />
        <Route path="/impressum" element={<LegalView />} />
        <Route path="/legal" element={<LegalView />} />
        <Route path="/datenschutz" element={<LegalView />} />
        <Route path="/privacy" element={<LegalView />} />
        <Route path="/agb" element={<LegalView />} />
        <Route path="/terms" element={<LegalView />} />
        <Route path="/konto-loeschen" element={<LegalView />} />
        <Route path="/delete-account" element={<LegalView />} />
      </Routes>
    )
  }

  if (mode === 'loading') {
    return (
      <div className="min-h-dvh flex items-center justify-center text-muted">
        {t('common.loading')}
      </div>
    )
  }
  if (mode === 'anonymous') return <LoginView />
  // Läuft für dieses Konto eine Löschvormerkung, kommt die Wahl „abbrechen /
  // beibehalten" vor allem anderen — auch vor dem Onboarding (Vorgang 019, AK5).
  if (mode === 'loeschung') return <KontoLoeschungGate />
  if (mode === 'onboarding') return <OnboardingView />
  if (mode === 'avv') return <AvvGate />

  return (
    <div className="app-schale h-dvh flex flex-col sm:flex-row overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden sm:flex sm:flex-col w-56 shrink-0 border-r border-border bg-surface">
        <div className="px-4 py-4 border-b border-border flex items-center gap-2.5">
          <Logo size={30} />
          <div className="min-w-0">
          <div className="font-semibold leading-tight">{t('app.name')}</div>
          <div className="text-xs text-muted truncate">
            {store.db.tenant.name || t('common.loading')}
          </div>
          </div>
        </div>
        <nav className="p-2 flex-1 space-y-1">
          {NAV.map(({ to, key, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cx('flex items-center gap-3 px-3 py-2 rounded-xl text-sm',
                  isActive ? 'bg-accent-soft text-accent font-medium' : 'text-muted hover:bg-surface-2')
              }
            >
              <Icon size={18} />
              {t(key)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          {/* Kundennummer, Version und die beiden Pflichtseiten — dort, wo man sie
              sucht, und ohne dafür in die Einstellungen wechseln zu müssen. */}
          <div className="text-[10px] text-muted text-center space-y-0.5">
            {store.db.tenant.customer_number && (
              <div className="tabular-nums">
                {t('account.customerNumber')} {store.db.tenant.customer_number}
              </div>
            )}
            <div className="tabular-nums">v{__APP_VERSION__}</div>
            <div className="flex justify-center gap-2">
              <NavLink to="/impressum" className="hover:text-fg underline underline-offset-2">
                {t('account.imprint')}
              </NavLink>
              <NavLink to="/datenschutz" className="hover:text-fg underline underline-offset-2">
                {t('account.privacy')}
              </NavLink>
              <NavLink to="/agb" className="hover:text-fg underline underline-offset-2">
                {t('account.terms')}
              </NavLink>
            </div>
          </div>
          <div className="flex gap-1" role="group" aria-label="Language">
            {(['de', 'en'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={cx(
                  'flex-1 py-1.5 rounded-lg text-xs font-medium uppercase',
                  lang === l ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-muted',
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto pb-20 sm:pb-0">
        {mode === 'demo' && <DemoBanner />}
        {mode === 'live' && <TrialBanner />}
        {mode === 'live' && <AvvBanner />}
        {store.error && <ErrorBanner message={store.error} onClose={store.clearError} />}
        <Routes>
          <Route path="/" element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<TodayView />} />
          <Route path="/calendar" element={<CalendarView />} />
          <Route path="/bookings" element={<BookingsView />} />
          <Route path="/guests" element={<GuestsView />} />
          <Route path="/billing" element={<BillingView />} />
          {/* Der Abo-Weg des Vermieters — in der App-Hülle gibt es ihn nicht.
              Warum, steht in `lib/kaufweg.ts`. `/billing` selbst bleibt: das ist
              die Rechnungsstellung an den GAST und damit ein anderer Geldfluss. */}
          {kaufwegSichtbar() && <Route path="/billing/setup" element={<BillingSetupView />} />}
          {kaufwegSichtbar() && <Route path="/billing/return" element={<BillingSetupView />} />}
          {/* Der LADEN-Kaufweg (Vorgang 009, Schnitt 8): nur in der iOS-Hülle,
              nur wenn der Kaufweg live ist oder es der Sandkasten-Prüfbuild
              ist — der gewöhnliche Auslieferungsbuild zeigt ihn nicht (AK1).
              Der Serverschalter `kaufweg_offen` greift zusätzlich im Blatt. */}
          {ladenKaufwegSichtbar() && <Route path="/billing/laden" element={<LadenAboView />} />}
          <Route path="/settings" element={<SettingsView />} />
          {/* Stripe returns the landlord here with ?code=&state=; the card on the
              settings screen finishes the handshake. */}
          <Route path="/settings/stripe/return" element={<SettingsView />} />
          {/* Vor der allgemeinen Belegroute: „bundle" wäre sonst ein `kind`. */}
          <Route path="/document/bundle/:from/:to" element={<InvoiceBundle />} />
          <Route path="/document/:kind/:id" element={<DocumentView />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </main>

      {/* Mobile tab bar */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-border pb-safe">
        {/* Sechs statt fünf Spalten, seit die Gastkartei dazugekommen ist.
            Auf einem 375 Pixel breiten Telefon bleiben rund 62 Pixel je Eintrag, und
            „Einstellungen" ist länger als das. Die Alternative wäre ein „Mehr"-Menü
            als sechster Eintrag gewesen — das versteckt eine Hauptansicht hinter
            einem zusätzlichen Tipp, und wer die Einstellungen sucht, sucht sie dann
            erst recht. Also: kleinere Schrift, und das Wort darf umbrechen. Zwei
            Zeilen sind lesbar, ein abgeschnittenes „Einstellun…" ist es nicht. */}
        <div className="grid grid-cols-6">
          {NAV.map(({ to, key, short, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              // Der volle Name bleibt als Vorlesetext erhalten: „Kasse" ist kurz
              // genug für die Leiste, aber „Abrechnung" ist das, was gemeint ist.
              aria-label={t(key)}
              className={({ isActive }) =>
                cx('flex flex-col items-center gap-1 py-2 px-0.5',
                  'text-[11px] leading-none tracking-tight text-center whitespace-nowrap min-w-0',
                  isActive ? 'text-accent font-semibold' : 'text-muted')
              }
            >
              <Icon size={22} className="shrink-0" strokeWidth={2.1} />
              <span>{t(short ?? key)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}

function DemoBanner() {
  const { lang } = useI18n()
  const { signOut } = useAuth()
  return (
    <div className="no-print bg-accent-soft text-accent text-sm px-4 py-2 flex items-center gap-3">
      {/*
        * „Browser" stimmt nur im Browser.
        *
        * Der Satz erklärt, wo die Demodaten liegen: im `localStorage`, also
        * genau dort, wo man gerade ist. Im Browser ist das der Browser, in der
        * App das Gerät. Auf iPhone, iPad und Android stand bis zum 31.07.2026
        * „in diesem Browser" — in einer App ein Wort, das es dort nicht gibt.
        */}
      <span className="flex-1">
        {Capacitor.isNativePlatform()
          ? (lang === 'de'
            ? 'Demo mit Beispieldaten — Änderungen bleiben nur auf diesem Gerät.'
            : 'Demo with sample data — changes stay on this device only.')
          : (lang === 'de'
            ? 'Demo mit Beispieldaten — Änderungen bleiben nur in diesem Browser.'
            : 'Demo with sample data — changes stay in this browser only.')}
      </span>
      <button onClick={signOut} className="font-medium underline underline-offset-2">
        {lang === 'de' ? 'Anmelden' : 'Sign in'}
      </button>
    </div>
  )
}

/**
 * The trial, stated where it cannot be missed.
 *
 * It appears while there is no usable mandate, and again in the last week before the
 * trial ends. Silence is what made the first version dishonest: a landlord was let
 * into the product with no mention of a trial, a price, or a payment method.
 */
function TrialBanner() {
  const { state } = useBillingState()
  const { t, lang, formatMoney } = useI18n()
  const stand = usePaketStand()
  const de = lang === 'de'
  if (!state) return null

  /*
   * ══════════════════════════════════════════════════════════════════════════
   *  EINE STELLE, DIE AUS VIER FELDERN EINE AUSSAGE MACHT
   * ══════════════════════════════════════════════════════════════════════════
   *
   * Hier stand bis zum 03.08.2026 eine handgerechnete Fassung: `hasMandate`,
   * `left` und `expired` wurden aus `status`, `trial_ends_at`, `mandate_status`
   * und `default_payment_method` neu abgeleitet — während `billingStage()` in
   * `data/stripe.ts` genau das schon tat und **keinen einzigen Aufrufer
   * ausserhalb seines eigenen Tests hatte**.
   *
   * Zwei Fassungen derselben Regel, und die gepflegte war die ungetestete. Der
   * Fehler, den das erzeugt, ist nicht theoretisch: Ein Vermieter, der bezahlt
   * hat, liest „Testphase beendet", weil die eine Fassung `status` anders
   * gewichtet als die andere.
   *
   * Jetzt rechnet das Band nicht mehr. Es fragt.
   */
  const stufe = billingStage(state)
  const left = trialDaysLeft(state)

  // Bezahlt und über die Testphase hinaus: schweigen. Die Abozeile darf noch
  // eine Weile `trialing` lesen — die Umstellung ist Buchhaltung im CRM —, und
  // einem zahlenden Kunden herunterzuzählen ist die Lüge, die dieses Band
  // früher erzählt hat.
  if (stufe === 'active' || stufe === 'trial_mandate_ok') {
    if (left === null || left === 0 || left > 7) return null
  }
  if (stufe === 'canceled' || stufe === 'paused') return null

  /*
   * In der App-Hülle darf hier weder ein Preis noch eine Aufforderung stehen —
   * 3.1.3(f) verbietet „calls to action for purchase outside of the app", und
   * ein genannter Preis ist bereits einer. Die TATSACHE, dass die Testphase
   * endet und das Konto danach nur noch lesend ist, bleibt trotzdem stehen:
   * sie wegzulassen wäre die Unehrlichkeit, die dieses Band überhaupt erst
   * nötig gemacht hat.
   */
  const kaufweg = kaufwegSichtbar()

  // Der Betrag kommt aus dem gebuchten Paket, nicht aus einer Zahl an dieser
  // Stelle. Ist keiner zu ermitteln, bleibt der Satz OHNE Preis — ein
  // Ersatzwert wäre eine erfundene Zahl dort, wo ein Vermieter am ehesten
  // hinsieht.
  const jeMonat = stand.paket ? preisJeMonat(stand.paket, state.contract_term) : null
  const preisSatz = jeMonat !== null && stand.paket
    ? (de
      ? ` Danach ${formatMoney(jeMonat, stand.paket.currency)} pro Monat zzgl. USt.`
      : ` Then ${formatMoney(jeMonat, stand.paket.currency)} per month plus VAT.`)
    : ''
  const endDate = state.trial_ends_at
    ? new Intl.DateTimeFormat(de ? 'de-AT' : 'en-GB', { day: 'numeric', month: 'long' })
      .format(new Date(state.trial_ends_at))
    : ''

  /*
   * `trial_failed` bekommt seinen EIGENEN Satz, und das ist der Kern dieses
   * Schnitts. Vorher fiel dieser Fall unter „Zahlungsdaten fehlen" — dieselbe
   * Auskunft wie für jemanden, der es noch nie versucht hat.
   *
   * Die beiden brauchen aber verschiedene Handlungen: Der eine muss es zum
   * ersten Mal tun, der andere ES NOCH EINMAL. Und der zweite glaubt, er habe
   * es längst erledigt — genau deshalb ist ihm seit dem 27.07.2026 nicht
   * aufgefallen, dass sein Mandat gescheitert ist.
   */
  const tone =
    stufe === 'trial_expired' || stufe === 'past_due' ? 'bg-danger-soft text-danger'
    : stufe === 'trial_failed' ? 'bg-danger-soft text-danger'
    : stufe === 'trial_mandate_ok' || stufe === 'active' ? 'bg-accent-soft text-accent'
    : 'bg-warn-soft text-warn'

  const text =
    stufe === 'trial_failed'
      // Der GRUND steht in `mandate_last_error` und war bis heute unsichtbar.
      // „Es ist fehlgeschlagen" beantwortet nicht, was zu tun ist — „das Fenster
      // war abgelaufen" schon.
      // Die Form stammt aus H4 und ist nicht beliebig: ANLAUF, GRUND, FRIST.
      //
      // „Ihr letzter Versuch ist fehlgeschlagen" sagt, dass es einen gab — das
      // unterscheidet diesen Vermieter von dem, der es nie probiert hat, und
      // genau diese Unterscheidung hat bis heute gefehlt. Dann der Grund. Dann
      // das DATUM: „mit der Testphase" ist keine Frist, sondern eine
      // Umschreibung, und wer sie liest, weiss danach nicht, wie viel Zeit er
      // hat.
      ? (de
        ? `Ihr letzter Versuch ist fehlgeschlagen: ${mandatFehlerSatz(state.mandate_last_error, true)}`
          // „Ihr Zugang endet" sagt die halbe Wahrheit und die haerte Haelfte.
          // Am 03.08.2026 in der laufenden Huelle aufgefallen: Alle anderen
          // Zustaende nennen Lesezugriff UND Datenerhalt, nur `trial_failed`
          // nicht — der Zustand, den ein Vermieter kurz vor der Sperre liest.
          // Genau der fragt sich, ob seine Buchungen weg sind.
          + (endDate
            ? ` Ohne hinterlegtes Zahlungsmittel ist Ihr Konto ab dem ${endDate} nur noch lesend — Ihre Daten bleiben erhalten.`
            : ' Ohne hinterlegtes Zahlungsmittel ist Ihr Konto nach der Testphase nur noch lesend — Ihre Daten bleiben erhalten.')
        : `Your last attempt failed: ${mandatFehlerSatz(state.mandate_last_error, false)}`
          + (endDate
            ? ` Without a payment method on file your account becomes read-only on ${endDate} — your data is kept.`
            : ' Without a payment method on file your account becomes read-only after the trial — your data is kept.'))
    : stufe === 'past_due'
      ? (de
        ? 'Eine Zahlung ist ausgeblieben. Sie haben nach unserer Mahnung 14 Tage Zeit; danach ist der Zugang nur noch lesend. Ihre Daten bleiben erhalten.'
        : 'A payment failed. You have 14 days after our reminder; after that access is read-only. Your data is kept.')
    : stufe === 'trial_expired'
      ? (kaufweg
        ? (de
          ? 'Testphase beendet — nur noch Lesezugriff. Ihre Daten bleiben erhalten; mit hinterlegten Zahlungsdaten geht es sofort weiter.'
          : 'Trial over — read-only. Your data is kept; adding payment details reopens it immediately.')
        : (de
          ? 'Testphase beendet — dieses Konto ist auf Lesezugriff gesetzt. Ihre Daten bleiben erhalten.'
          : 'Trial over — this account is read-only. Your data is kept.'))
    : stufe === 'trial_pending'
      ? (de
        ? 'Ihr Zahlungsmittel wird gerade bestätigt. Bei SEPA kann das einen Moment dauern.'
        : 'Your payment method is being confirmed. With SEPA this can take a moment.')
    : stufe === 'trial_no_mandate'
      ? (left !== null
        ? (de
          ? `Testphase: noch ${left} ${left === 1 ? 'Tag' : 'Tage'} (bis ${endDate}). Zahlungsdaten fehlen — danach ist nur noch Lesezugriff möglich.`
          : `Trial: ${left} ${left === 1 ? 'day' : 'days'} left (until ${endDate}). No payment details yet — after that it is read-only.`)
        : (de ? 'Zahlungsdaten fehlen.' : 'No payment details on file.'))
    : (kaufweg
      ? (de ? `Testphase endet am ${endDate}.${preisSatz}` : `Trial ends on ${endDate}.${preisSatz}`)
      : (de ? `Testphase endet am ${endDate}.` : `Trial ends on ${endDate}.`))

  // Wer es schon einmal versucht hat, liest „Erneut versuchen" — nicht „Jetzt
  // hinterlegen". Der Unterschied ist der ganze Punkt.
  const knopf =
    stufe === 'trial_failed' || stufe === 'past_due'
      ? (de ? 'Erneut versuchen' : 'Try again')
      : stufe === 'trial_mandate_ok' || stufe === 'active'
        ? (de ? 'Tarif ansehen' : 'View plan')
        : (de ? 'Jetzt hinterlegen' : 'Add now')

  return (
    <div className={cx('no-print text-sm px-4 py-2 flex items-center gap-3', tone)}>
      <span className="flex-1">{text}</span>
      {kaufweg && (
        <NavLink to="/billing/setup" className="font-medium underline underline-offset-2 shrink-0">
          {knopf}
        </NavLink>
      )}
      {/* Der Laden-Einstieg (Vorgang 009, Schnitt 8): erscheint nur, wenn die
          Hülle den Laden-Kaufweg zeigt — also live geschaltet oder im
          Sandkasten-Prüfbuild. Der Knopftext nennt keinen Betrag; der Preis
          steht ausschließlich im Kaufblatt und kommt aus dem Laden (AK32). */}
      {!kaufweg && ladenKaufwegSichtbar() && (
        <NavLink to="/billing/laden" className="font-medium underline underline-offset-2 shrink-0">
          {t('abo.laden.titel')}
        </NavLink>
      )}
    </div>
  )
}

function ErrorBanner({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div className="no-print bg-danger-soft text-danger text-sm px-4 py-2 flex items-start gap-3">
      <span className="flex-1">{message}</span>
      <button onClick={onClose} aria-label="Close"><X size={16} /></button>
    </div>
  )
}
